import {
  createRouter,
  createRootRoute,
  createRoute,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router'
import { AppShell } from '@/components/app-shell'
import { AuthGate } from '@/components/auth-gate'
import { Skeleton } from '@/components/ui'

// Auth surfaces stay eager (first paint); every page behind the shell is
// route-level code-split via lazyRouteComponent (named-export aware).
import { LoginPage } from '@/pages/auth/login'
import { SignUpPage } from '@/pages/auth/signup'
import { ForgotPasswordPage } from '@/pages/auth/forgot-password'
import { ResetPasswordPage } from '@/pages/auth/reset-password'
import { OrgChooserPage } from '@/pages/orgs/chooser'

const OrgChoicePage = lazyRouteComponent(() => import('@/pages/orgs/choice'), 'OrgChoicePage')
const OrgSetupPage = lazyRouteComponent(() => import('@/pages/orgs/setup'), 'OrgSetupPage')
const InviteAcceptPage = lazyRouteComponent(() => import('@/pages/invite'), 'InviteAcceptPage')
const DashboardPage = lazyRouteComponent(() => import('@/pages/dashboard'), 'DashboardPage')
const EnvsListPage = lazyRouteComponent(() => import('@/pages/envs/list'), 'EnvsListPage')
const EnvDetailPage = lazyRouteComponent(() => import('@/pages/envs/detail'), 'EnvDetailPage')
const EnvNewPage = lazyRouteComponent(() => import('@/pages/envs/new'), 'EnvNewPage')
const SnapshotsListPage = lazyRouteComponent(() => import('@/pages/snapshots/list'), 'SnapshotsListPage')
const SnapshotRunDetailPage = lazyRouteComponent(
  () => import('@/pages/snapshots/run-detail'),
  'SnapshotRunDetailPage',
)
const FindingsPage = lazyRouteComponent(() => import('@/pages/findings'), 'FindingsPage')
const AccessTrailPage = lazyRouteComponent(() => import('@/pages/access-trail'), 'AccessTrailPage')
const IamAnalyticsPage = lazyRouteComponent(
  () => import('@/pages/iam-analytics'),
  'IamAnalyticsPage',
)
const AnalyzePage = lazyRouteComponent(() => import('@/pages/analyze'), 'AnalyzePage')
const IdentitiesPage = lazyRouteComponent(() => import('@/pages/identities'), 'IdentitiesPage')
const FileSharingPage = lazyRouteComponent(() => import('@/pages/file-sharing'), 'FileSharingPage')
const AccessRequestsPage = lazyRouteComponent(
  () => import('@/pages/access-requests'),
  'AccessRequestsPage',
)
const OrganizationPage = lazyRouteComponent(
  () => import('@/pages/organization'),
  'OrganizationPage',
)
const ReportsPage = lazyRouteComponent(() => import('@/pages/reports'), 'ReportsPage')
const SettingsPage = lazyRouteComponent(() => import('@/pages/settings'), 'SettingsPage')
const FeedbackPage = lazyRouteComponent(() => import('@/pages/feedback'), 'FeedbackPage')
const WalkthroughPage = lazyRouteComponent(() => import('@/pages/walkthrough'), 'WalkthroughPage')

/** Calm skeleton while a route chunk loads (cached after first visit). */
function RoutePending() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-4)' }} aria-busy="true">
      <Skeleton width={280} height={28} />
      <Skeleton width={460} height={16} />
      <Skeleton width="100%" height={360} />
    </div>
  )
}

// ───────────────────────────────────────────────────────────────
// Route tree
// ───────────────────────────────────────────────────────────────

const rootRoute = createRootRoute({
  component: () => <Outlet />,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
})

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  component: SignUpPage,
})

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
})

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
})

// Pathless layout for the onboarding funnel: gated by AuthGate (render-time,
// so it waits out the async OIDC session probe) but WITHOUT the AppShell —
// these pages carry their own chrome.
const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_onboarding',
  component: () => (
    <AuthGate>
      <Outlet />
    </AuthGate>
  ),
})

const orgChooserRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: '/orgs',
  component: OrgChooserPage,
})

const orgChoiceRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: '/orgs/choice',
  component: OrgChoicePage,
})

const orgSetupRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: '/orgs/new',
  component: OrgSetupPage,
})

// Public — reachable without a session (invite-email links land here for
// signed-out visitors), so it hangs off `rootRoute` directly rather than
// the AuthGate-wrapped `onboardingRoute`. The page itself branches on
// `useAuth()` session state.
const inviteAcceptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite',
  component: InviteAcceptPage,
  validateSearch: (search): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
})

// All authenticated routes live under the AppShell layout.
// The underscore-prefixed id marks this as a pathless layout route — child
// routes keep their own paths un-prefixed.
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  component: () => (
    <AuthGate>
      <AppShell>
        <Outlet />
      </AppShell>
    </AuthGate>
  ),
})

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: () => {
    // Resume onboarding after the post-create silent re-auth — the refreshed
    // token now carries the new org's group, so jump back into the wizard.
    const resume = sessionStorage.getItem('it.resumeOnboarding')
    if (resume) {
      sessionStorage.removeItem('it.resumeOnboarding')
      throw redirect({
        to: '/orgs/new',
        search: { step: Number(resume) } as unknown as Record<string, never>,
      })
    }
    // Otherwise enter through the org chooser: it auto-forwards to /dashboard
    // when the user has exactly one org, and shows the create-or-join funnel
    // when they have none (fresh self-registered accounts).
    throw redirect({ to: '/orgs' })
  },
})

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/dashboard',
  component: DashboardPage,
})

const envsListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/envs',
  component: EnvsListPage,
})
const envNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/envs/new',
  component: EnvNewPage,
})
const envDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/envs/$envId',
  component: EnvDetailPage,
})

const snapshotsListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/snapshots',
  component: SnapshotsListPage,
})
const snapshotRunDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/snapshotruns/$runId',
  component: SnapshotRunDetailPage,
})

const findingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/findings',
  component: FindingsPage,
})

const accessTrailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/access-trail',
  component: AccessTrailPage,
})

const iamAnalyticsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/iam-analytics',
  component: IamAnalyticsPage,
})

const analyzeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/analyze',
  component: AnalyzePage,
})

// Audit Trail merged into Access Trail's events view — keep old URLs working.
const auditTrailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/audit-trail',
  beforeLoad: () => {
    const search: Record<string, unknown> = { view: 'events' }
    throw redirect({ to: '/access-trail', search: search as Record<string, never> })
  },
})

const identitiesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/identities',
  component: IdentitiesPage,
})

const fileSharingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/file-sharing',
  component: FileSharingPage,
})

const accessRequestsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/access-requests',
  component: AccessRequestsPage,
})

const organizationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/organization',
  component: OrganizationPage,
})

const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports',
  component: ReportsPage,
})

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  component: SettingsPage,
})

const feedbackRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/feedback',
  component: FeedbackPage,
})

const walkthroughRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/walkthrough/$provider',
  component: WalkthroughPage,
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  signUpRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  inviteAcceptRoute,
  onboardingRoute.addChildren([orgChooserRoute, orgChoiceRoute, orgSetupRoute]),
  appRoute.addChildren([
    indexRoute,
    dashboardRoute,
    envsListRoute,
    envNewRoute,
    envDetailRoute,
    snapshotsListRoute,
    snapshotRunDetailRoute,
    findingsRoute,
    accessTrailRoute,
    iamAnalyticsRoute,
    analyzeRoute,
    identitiesRoute,
    fileSharingRoute,
    accessRequestsRoute,
    organizationRoute,
    auditTrailRoute,
    reportsRoute,
    settingsRoute,
    feedbackRoute,
    walkthroughRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPendingComponent: RoutePending,
  // A route's chunk is code-split; while it loads the router otherwise holds the
  // PREVIOUS page on screen until this threshold elapses (library default 1000ms
  // — the "old page lingers ~1s" bug). Drop it so the new-page skeleton appears
  // almost immediately; keep a small min so fast/cached navs don't flash it.
  defaultPendingMs: 120,
  defaultPendingMinMs: 250,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
